import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Form } from "react-bootstrap";
import {
  MdPlayArrow as PlayArrowIcon,
  MdPause as PauseIcon,
  MdVolumeUp as VolumeUpIcon,
  MdVolumeDown as VolumeDownIcon,
  MdExpandMore as ExpandMoreIcon,
} from "react-icons/md";
import { useRouter } from "next/router";

import { useNowPlaying } from "@/components/contexts/now-playing";
import { usePlayerState } from "@/components/contexts/player";
import { displayArtist, recentlyPlayed } from "src/lib/now-playing";

import styles from "./Player.module.css";

const METER_BARS = [0, 140, 280, 90, 220, 60, 310];
const RECENT_LIMIT = 5;

function Player() {
  const {
    isLoading,
    isPlaying,
    playbackError,
    toggleLivestream,
    volume,
    adjustVolume,
  } = usePlayerState();
  const { song: currentSong, playlist } = useNowPlaying();
  const router = useRouter();

  const artist = displayArtist(currentSong);
  const hasShowLink = Boolean(playlist.show.id);

  const recent = recentlyPlayed(playlist, currentSong, RECENT_LIMIT);
  const [showRecent, setShowRecent] = useState(false);
  const recentRef = useRef(null);

  // Closes on an outside click or Escape, same as any other dropdown — it
  // isn't a modal, so anything else the visitor does should just dismiss it.
  useEffect(() => {
    if (!showRecent) return undefined;

    const handlePointer = (event) => {
      if (!recentRef.current?.contains(event.target)) setShowRecent(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setShowRecent(false);
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showRecent]);

  let playerIcon;
  if (isLoading) {
    playerIcon = (
      <span className="spinner-border spinner-border-sm" role="status">
        <span className="visually-hidden">Loading...</span>
      </span>
    );
  } else if (isPlaying) {
    playerIcon = <PauseIcon aria-hidden="true" />;
  } else {
    playerIcon = <PlayArrowIcon aria-hidden="true" />;
  }

  // "Ready" described the player. Before you press anything the useful thing to
  // say is what pressing it gets you.
  let status = "Listen live";
  if (isLoading) status = "Connecting";
  else if (playbackError) status = "Off air";
  else if (isPlaying) status = "Broadcasting";

  const handleShowClick = (event, showUrl) => {
    event.preventDefault();
    router.push(showUrl);
  };

  return (
    <div className={styles.playerBar}>
      <button
        type="button"
        className={styles.playButton}
        aria-label={isPlaying ? "Pause live stream" : "Play live stream"}
        aria-pressed={isPlaying}
        disabled={isLoading}
        onClick={toggleLivestream}
      >
        {playerIcon}
      </button>

      <div className={styles.nowPlaying}>
        <div className={styles.statusRow}>
          {/* Only while audio is actually flowing — at rest it read as seven
              specks of dust rather than a meter, and its arrival is now the
              cue that playback started. Decorative: not a reading of the
              audio. */}
          {isPlaying ? (
            <span className={styles.meter} aria-hidden="true">
              {METER_BARS.map((delay) => (
                <span key={delay} style={{ animationDelay: `${delay}ms` }} />
              ))}
            </span>
          ) : null}
          <span className={styles.status}>{status}</span>
          {currentSong.show ? (
            <>
              <span className={styles.statusSep}>&mdash;</span>
              {hasShowLink ? (
                <a
                  className={styles.showLink}
                  href={playlist.show.url}
                  onClick={(event) => handleShowClick(event, playlist.show.url)}
                >
                  {currentSong.show}
                </a>
              ) : (
                <span className={styles.showName}>{currentSong.show}</span>
              )}
            </>
          ) : null}
        </div>

        <p className={styles.trackLine}>
          {playbackError ? (
            <span className={styles.error}>{playbackError}</span>
          ) : (
            <>
              <span className={styles.track}>{currentSong.track}</span>
              {artist ? (
                <span className={styles.artist}> &middot; {artist}</span>
              ) : null}
            </>
          )}
        </p>
      </div>

      <div className={styles.volumeControl}>
        <VolumeDownIcon aria-hidden="true" />
        <Form.Range
          min={0}
          max={1}
          step={0.01}
          value={volume || 0}
          onChange={(e) => adjustVolume(Number(e.target.value))}
          aria-label="Volume"
        />
        <VolumeUpIcon aria-hidden="true" />
      </div>

      {/* The only place "recently played" lives — no separate homepage
          section repeating it. Hidden entirely rather than shown empty when
          the log has nothing yet (automation just started, or a fresh set). */}
      {recent.length > 0 ? (
        <div className={styles.recentWrap} ref={recentRef}>
          <button
            type="button"
            className={styles.recentToggle}
            aria-expanded={showRecent}
            aria-label="Recent"
            onClick={() => setShowRecent((open) => !open)}
          >
            <span className={styles.recentToggleLabel}>Recent</span>
            <ExpandMoreIcon
              className={showRecent ? styles.recentIconOpen : undefined}
              aria-hidden="true"
            />
          </button>

          {showRecent ? (
            <div className={styles.recentPanel}>
              <span className={styles.recentLabel}>Recently played</span>
              <ul className={styles.recentList}>
                {recent.map((entry) => (
                  <li key={entry.id} className={styles.recentItem}>
                    <span className={styles.recentTitle}>{entry.title}</span>
                    <span className={styles.recentArtist}>{entry.artist}</span>
                  </li>
                ))}
              </ul>
              <Link
                href="/history/"
                className={styles.recentHistory}
                onClick={() => setShowRecent(false)}
              >
                Full history →
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default Player;
